import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { ALLOWED_TABS, TABS } from '../../../data/directory/topic_classes/constants';
import { UK_SINGLE_COMPANIE } from '../../../data/directory/topic_classes/topicClassTypes';
import { CAPITAL_STRUCTURE } from '../../../data/directory/topic_classes/tabPaths';
import CompaniesSvc from '../../../services/dbServices/CompaniesSvc';
import {
  resetAllSearch,
} from '../../../actions/searchActions';
import { getPageMetaData } from '../../../services/dbServices/userServices';
import { getMetaTag } from '../../../helpers/helpers';

import Preloader from '../../Preloader';
import SingleCompanieHeader from './SingleCompanieHeader';
import TopicTab from '../../SEO_pages/TopicTabBodyCommon/TopicTab';
import SearchResult from '../../SearchPage/SearchResult';
import ProviderFilings from '../../Filing/ProviderFilingsAll/ProviderBody';
import CapitalStructure from '../../SEO_pages/Topic/CapitalStructure';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const SingleCompanie = (props) => {
  const { history, location, match } = props;
  let mounted = true;
  const initialized = useRef(false);
  const tabsBar = useRef(false);
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActieTabId] = useState(9);
  const [currentCompany, setCurrentCompany] = useState({
    loading: true,
    data: null,
  });
  const [identifier, setIdentifier] = useState('');
  const mainWidth = useSelector(({ common }) => (
    common.width
  ));
  const dispatch = useDispatch();
  const { params: { company_number: companyNumber } } = match;

  const removeTabFromUrl = () => {
    const currentUrl = location.pathname;
    return currentUrl.split('/').slice(0, 6).join('/');
  };

  const callAPI = () => {
    CompaniesSvc
      .getCompanyByIdentifier({ identifier: `NO:${companyNumber}|CC:GB` })
      .then((companyInfo) => {
        if (!companyInfo) {
          window.location.href = '/404';
          return;
        }
        initialized.current = true;
        let searchString = `NO:${companyInfo.companyNumber}`;
        if (companyInfo.source) {
          searchString += `|SR:${companyInfo.source}`;
        }
        if (!mounted) return;
        setIdentifier(searchString);
        setCurrentCompany({
          loading: false,
          data: companyInfo,
        });
      });
  };

  const callMetadataAPI = () => {
    const { path } = tabs.find((tab) => tab.active === true) || {};
    if (!identifier || !path || currentCompany.loading) return;

    getPageMetaData(`~directory/companies-insights/uk/companies/%/${path}`)
      .then((metaData) => {
        Object.values(metaData).forEach(({ title, meta_tags: metatags }) => {
          if (!metatags || !metatags.length) {
            return;
          }
          const hasTitle = metatags.find((f) => f.name === 'title');
          if (!hasTitle) {
            metatags.push({
              name: 'title',
              content: title,
            });
          }
          for (let i = 0; i < metatags.length; i += 1) {
            const { name, content, property } = metatags[i];
            const formatedText = content
              .replace('%{asset_name}', currentCompany.data?.name)
              .replace('%{asset_number}', currentCompany.data?.companyNumber)
              .replace('%{updated_date}', moment.utc().format('DD MMM YYYY'));
            if (name === 'title') {
              document.title = formatedText;
            }
            if (property) {
              getMetaTag('property', property).setAttribute('content', formatedText);
            } else {
              getMetaTag('name', name).setAttribute('content', formatedText);
            }
          }
        });
      });
  };

  useEffect(() => {
    callMetadataAPI();
  }, [activeTabId, identifier, currentCompany.loading]);

  const changeTab = (id) => {
    const allTabs = tabs.map((tab) => ({
      ...tab,
      active: tab.id === id,
    }));
    const { path } = allTabs.find((tab) => tab.active === true);
    const basePath = removeTabFromUrl();
    history.push(`${basePath}/${path}`);
    setTabs(allTabs);
    setActieTabId(id);
  };

  useEffect(() => {
    callAPI();
    const { params: urlParams } = match;

    const { tabs: allowedTabs } = ALLOWED_TABS.find(
      ({ type }) => type === UK_SINGLE_COMPANIE,
    );

    const tabs = (TABS
      .filter((tab) => allowedTabs.includes(tab.id))
      .map((tab) => ({ ...tab, active: tab.path === urlParams.page })));
    const { id } = tabs.find((tab) => tab.active === true) || { };
    if (!id) {
      const basePath = removeTabFromUrl();
      history.push(`${basePath}/${CAPITAL_STRUCTURE}`);
      const findTabIndex = tabs.findIndex((f) => f.path === CAPITAL_STRUCTURE);
      tabs[findTabIndex].active = true;
    }
    setTabs(tabs);
    setActieTabId(id || 9);

    window.scrollTo(0, 0);

    return () => {
      mounted = false;
      dispatch(resetAllSearch());
    };
  }, []);

  useEffect(() => {
    if (!initialized.current) {
      return;
    }
    setCurrentCompany({
      loading: true,
      data: null,
    });
    callAPI();
  }, [companyNumber]);

  if (currentCompany.loading) {
    return (
      <div className={cx('single-companie')}>
        <Preloader loading static centered />
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTabId) {
      case 1:
        return (
          <div className={cx('single-companie-news')}>
            <SearchResult
              investorPage
              isNewPrivateCompanies
              searchQueryFromUrl={currentCompany.data?.name ? `'${currentCompany.data?.name}'` : ''}
              isMobile={mainWidth <= 768}
            />
          </div>
        );
      case 2:
        return (
          <ProviderFilings
            hasSourceAccess
            hasTabHeader
            isCompany
            extraProps={{ identifier }}
            // piwikEnabled={piwikEnabled}
          />
        );
      case 9:
      default:
        return (
          <CapitalStructure
            isDirectNumberPassed
            companyNumber={currentCompany.data?.companyNumber}
          />
        );
    }
  };

  return (
    <div className={cx('single-companie')}>
      <SingleCompanieHeader
        currentCompany={currentCompany}
      />

      <TopicTab
        tabs={tabs}
        changeTab={changeTab}
        activeTab={activeTabId}
        ref={tabsBar}
        // className={cx('single-companie-tabs')}
      />
      <div className={cx('single-companie-body')}>
        { renderContent() }
      </div>
    </div>
  );
};

export default SingleCompanie;
